import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import MenuChecklist from './MenuChecklist';
import { getMenus, createCampaign } from '../../services/api';

export default function NewCampaignDialog({ open, onOpenChange, channelId }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open || !channelId) return undefined;
    let cancelled = false;
    setName('');
    setSelected({});
    setLoading(true);
    getMenus(channelId)
      .then((data) => {
        if (cancelled) return;
        setMenus((data.menus || []).filter((m) => m.status !== 'replaced' && !m.campaignId));
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load menus'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, channelId]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const menuIds = menus.filter((m) => selected[m.id]).map((m) => m.id);
      const campaign = await createCampaign({ channelId, name: trimmed, menuIds, defaultMenuId: null });
      onOpenChange(false);
      navigate(`/campaigns/${campaign.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#18181B] border-[#27272A] max-w-lg">
        <DialogHeader>
          <DialogTitle>New campaign</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Name your campaign and choose the menus to launch together. You can change this later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="campaign-name">Name</Label>
            <Input
              id="campaign-name"
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Autumn sale"
              className="bg-[#09090B] border-[#27272A]"
              data-testid="new-campaign-name"
            />
          </div>
          <div className="space-y-2">
            <Label>Menus</Label>
            <MenuChecklist
              menus={menus}
              loading={loading}
              selected={selected}
              onToggle={toggle}
              emptyText="No available menus. You can add menus after creating the campaign."
              testIdPrefix="new-campaign-menu"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-[#27272A]" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="btn-primary"
            onClick={handleCreate}
            disabled={!name.trim() || creating}
            data-testid="new-campaign-create"
          >
            {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
